import React, { useState, useEffect, useRef, useCallback } from "react";
import { api, extractOnePagerText } from "./api/client.js";
import { colors, font } from "./utils/theme.js";
import { callAnthropicDirect, PROXY_MESSAGES_URL, proxyHeaders } from "./utils/anthropicClient.js";

const LETTER_W = 816;
const LETTER_H = 1056;

// ─── Anthropic direct-call (bypasses Azure Functions to avoid SWA timeout) ───
const MODELS = [
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6", note: "Fast · Recommended" },
  { id: "claude-opus-4-7",   label: "Opus 4.7",   note: "Highest quality · Slower" },
];

const SYSTEM_PROMPT = `You are a senior designer at a world-class defense-industry creative agency. You produce two-page capability briefs that make program managers stop and read. Your output is original, polished, and specific to each company — never templated.

## Hard Constraints (non-negotiable)
- Return ONLY the complete HTML document starting with \`<!DOCTYPE html>\`. No markdown, no explanation.
- All CSS in one \`<style>\` block in \`<head>\`. Up to 2 Google Fonts via \`<link>\`. No JavaScript.
- No page numbers. No generic document-type labels ("CAPABILITY BRIEF · 2026", etc.).
- EXACTLY TWO \`<div class="page">\` elements — 816px × 1056px each, overflow:hidden, page-break-after:always on the first.
- Every element must have a fixed or max height so nothing overflows. Cut copy before overflowing.
- Images: use \`{{LOGO}}\` as the logo src on both pages. Use \`{{PRODUCT_1}}\`, \`{{PRODUCT_2}}\`, \`{{PRODUCT_3}}\` as product image src values only where they genuinely improve the design — you don't have to use all of them. Any image element must have explicit width AND height set in CSS or as attributes, with object-fit defined.
- Never inline base64. Never use placeholders outside of src attributes.

## Page Intent
**Page 1** is a visual hook. Its job is to stop a busy person and make them curious. It should feel more like a premium product page than a document — bold, spacious, image-forward. Minimal prose. The reader should be able to absorb it in under 10 seconds.

**Page 2** is technical depth. It earns the trust that Page 1 creates. It can and should be text-rich — specs, methodology, validation evidence, capability breakdown, team. Every claim on Page 1 gets substantiated here.

## Design Principles
Create something genuinely original. Make deliberate choices — typography, layout, color, hierarchy — that are specific to this company and its brand. A reader should be able to tell who made this brief from 10 feet away.

- Use the brand colors and fonts provided. If none are provided, derive a palette that feels right for this company's market and mission.
- One accent color used sparingly: stat numerals, key borders, rule lines.
- White space is intentional. Don't fill every pixel.
- Must read clearly in black-and-white print.

## Print CSS (include verbatim)
\`\`\`css
@media print {
  body { margin: 0; background: transparent; }
  .page { margin: 0 auto; box-shadow: none; page-break-after: always; }
  .page:last-child { page-break-after: auto; }
}
\`\`\``;

const VARIANT_INSTRUCTIONS = {
  A: `VARIANT A — IMPACT FIRST
Design around the product. If a product image is provided, it should be the dominant visual on Page 1 — not a thumbnail, not a sidebar element. Give it space. Build the layout around it. Page 1 should feel like a premium product launch page: bold, confident, mostly visual.

Page 2 earns the credibility. Go deep on how it works, what's been validated, specs and capabilities. Dense is fine here.

Make design choices that reflect this company's specific brand and positioning. The two variants should look and feel meaningfully different from each other.`,

  B: `VARIANT B — PRECISION AND CRAFT
Lead with structure and clarity. This variant should feel like the kind of brief a technical program evaluator would trust immediately — organized, data-forward, every element purposeful.

Page 1 still needs to visually command attention, but does it through tight grid work, strong typography, and purposeful use of product imagery rather than raw visual drama.

Page 2 is the technical showcase. Specs, capability breakdowns, validation data, feature comparisons — give it the depth a serious evaluator needs.

Make design choices that reflect this company's specific brand and positioning. The two variants should look and feel meaningfully different from each other.`,
};

function cleanHtml(raw) {
  let html = raw.trim();
  if (html.startsWith("```html")) html = html.slice(7);
  else if (html.startsWith("```")) html = html.slice(3);
  if (html.endsWith("```")) html = html.slice(0, -3);
  return html.trim();
}

function extractHtml(data) {
  const textBlocks = (data.content || []).filter((b) => b.type === "text");
  if (!textBlocks.length) throw new Error("No HTML content returned from Claude");
  const htmlBlock = [...textBlocks].reverse().find((b) => b.text.includes("<!DOCTYPE") || b.text.includes("<html"));
  return cleanHtml((htmlBlock || textBlocks[textBlocks.length - 1]).text);
}

function buildImageContent(pager) {
  const parts = [];
  if (pager.logoBase64 && !pager.logoBase64.startsWith("data:image/svg")) {
    const b64 = pager.logoBase64.replace(/^data:[^;]+;base64,/, "");
    parts.push({ type: "text", text: "COMPANY LOGO (use {{LOGO}} as the img src in the header of both pages):" });
    parts.push({ type: "image", source: { type: "base64", media_type: pager.logoMimeType || "image/png", data: b64 } });
  } else if (pager.logoBase64) {
    parts.push({ type: "text", text: "COMPANY LOGO is SVG. Use {{LOGO}} as the img src in headers." });
  }
  (pager.productImages || []).forEach((img, i) => {
    const b64 = img.base64.replace(/^data:[^;]+;base64,/, "");
    parts.push({ type: "text", text: `PRODUCT IMAGE ${i + 1}${img.caption ? ` — ${img.caption}` : ""} (placeholder: {{PRODUCT_${i + 1}}} — use only if it improves the design):` });
    parts.push({ type: "image", source: { type: "base64", media_type: img.mimeType || "image/jpeg", data: b64 } });
  });
  return parts;
}

function restorePlaceholders(html, pager) {
  let out = html;
  if (pager?.logoBase64) out = out.split(pager.logoBase64).join("{{LOGO}}");
  (pager?.productImages || []).forEach((img, i) => {
    if (img?.base64) out = out.split(img.base64).join(`{{PRODUCT_${i + 1}}}`);
  });
  return out;
}

// callAnthropicDirect (non-streaming) lives in src/utils/anthropicClient.js and calls
// the server-side proxy (which holds the Anthropic key); shared with PDF → Markdown.

// Streaming variant — calls onDelta(newText, accumulatedBlockText) for each text token.
// Optional onThinkingDelta(newText, accumulatedText) fires for extended thinking blocks.
// Handles multi-block responses (web search, thinking, text interspersed).
// Returns the cleaned HTML string when done.
async function callAnthropicStream(body, extraHeaders, onDelta, onThinkingDelta) {
  // Streams through the server-side proxy (key stays on the server). The proxy pipes
  // Anthropic's SSE straight back, so the parser below is unchanged.
  const res = await fetch(PROXY_MESSAGES_URL, {
    method: "POST",
    headers: await proxyHeaders(extraHeaders),
    body: JSON.stringify({ ...body, stream: true }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = `Anthropic API error: ${res.status}`;
    try { const e = JSON.parse(text); msg = e?.error?.message || msg; } catch (_) {}
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const blocks = {}; // index → { type, text }
  let stopReason = null;
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop(); // hold incomplete line
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const raw = line.slice(6).trim();
      if (raw === "[DONE]") continue;
      try {
        const ev = JSON.parse(raw);
        if (ev.type === "content_block_start") {
          const cb = ev.content_block;
          blocks[ev.index] = { type: cb.type, text: cb.thinking || cb.text || "" };
        } else if (ev.type === "content_block_delta") {
          const blk = blocks[ev.index];
          if (!blk) continue;
          if (ev.delta.type === "text_delta" && blk.type === "text") {
            blk.text += ev.delta.text;
            onDelta(ev.delta.text, blk.text);
          } else if (ev.delta.type === "thinking_delta" && blk.type === "thinking") {
            blk.text += ev.delta.thinking;
            if (onThinkingDelta) onThinkingDelta(ev.delta.thinking, blk.text);
          }
        } else if (ev.type === "message_delta" && ev.delta?.stop_reason) {
          stopReason = ev.delta.stop_reason;
        }
      } catch (_) {}
    }
  }

  if (stopReason === "max_tokens") throw new Error("Generation was truncated — reduce whitepaper length and try again.");

  // Pick the text block that contains the HTML (last one with DOCTYPE or <html)
  const textBlocks = Object.values(blocks).filter((b) => b.type === "text");
  const htmlBlock = [...textBlocks].reverse().find((b) => b.text.includes("<!DOCTYPE") || b.text.includes("<html"));
  return cleanHtml((htmlBlock || textBlocks[textBlocks.length - 1])?.text || "");
}

const s = {
  input: {
    width: "100%", padding: "10px 12px", background: colors.bg,
    border: `1px solid ${colors.borderLight}`, borderRadius: 6,
    color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  },
  label: {
    fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: 1,
    textTransform: "uppercase", display: "block", marginBottom: 6,
  },
  fieldWrap: { marginBottom: 18 },
  btnPrimary: {
    padding: "10px 22px", background: colors.accent, border: "none",
    borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 600,
    cursor: "pointer", fontFamily: font,
  },
  btnSecondary: {
    padding: "10px 22px", background: "transparent", border: `1px solid ${colors.borderLight}`,
    borderRadius: 8, color: colors.textMuted, fontSize: 14, fontWeight: 500,
    cursor: "pointer", fontFamily: font,
  },
  card: {
    background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: 12, padding: 24,
  },
  sectionHead: {
    fontSize: 11, fontWeight: 700, letterSpacing: 2, color: colors.textDim,
    textTransform: "uppercase", marginBottom: 14,
  },
};

// ─── Image compression ───
async function compressImage(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    if (file.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ base64: e.target.result, mimeType: "image/svg+xml" });
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height / width) * maxDim); width = maxDim; }
        else { width = Math.round((width / height) * maxDim); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve({ base64: canvas.toDataURL("image/jpeg", quality), mimeType: "image/jpeg" });
    };
    img.onerror = (err) => { URL.revokeObjectURL(objectUrl); reject(err); };
    img.src = objectUrl;
  });
}

// ─── Upload drop zone ───
function DropZone({ label, accept, multiple, onFiles, hint, files = [] }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    onFiles(multiple ? dropped : [dropped[0]]);
  };
  return (
    <div>
      <label style={s.label}>{label}</label>
      <div
        onClick={() => inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragging ? colors.accent : colors.borderLight}`,
          borderRadius: 8, padding: "20px 16px", textAlign: "center",
          cursor: "pointer", transition: "border-color 0.15s",
          background: dragging ? colors.accentDim : "transparent",
        }}
      >
        <div style={{ fontSize: 22, marginBottom: 6 }}>+</div>
        <div style={{ fontSize: 13, color: colors.textMuted }}>
          {files.length > 0
            ? files.map((f) => f.name || f).join(", ")
            : hint || "Click or drag file here"}
        </div>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: "none" }}
          onChange={(e) => onFiles(Array.from(e.target.files))} />
      </div>
    </div>
  );
}

// ─── Pager list row ───
function PagerRow({ pager, onOpen, onDelete }) {
  const status = pager.status || "draft";
  const statusColor = status === "ready" ? colors.success : status === "generating" ? colors.warning : colors.textDim;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 16, padding: "14px 0",
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>{pager.companyName || "Unnamed"}</div>
        <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>
          {pager.createdAt ? new Date(pager.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
          {pager.generationCount ? ` · ${pager.generationCount} generation${pager.generationCount !== 1 ? "s" : ""}` : ""}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: statusColor, textTransform: "uppercase" }}>
        {status}
      </span>
      <button onClick={() => onOpen(pager)} style={{ ...s.btnSecondary, padding: "7px 16px", fontSize: 13 }}>Open</button>
      <button onClick={() => onDelete(pager.id)} style={{
        background: "transparent", border: "none", color: colors.danger,
        fontSize: 13, cursor: "pointer", fontFamily: font, padding: "7px 8px",
      }}>✕</button>
    </div>
  );
}

// ─── Main component ───
export default function OnePagerBuilder({ user }) {
  const [view, setView] = useState("list");
  const [buildStep, setBuildStep] = useState(1);
  const [pagers, setPagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePager, setActivePager] = useState(null);
  const [variants, setVariants] = useState([]);
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeHtml, setActiveHtml] = useState("");
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingVariant, setGeneratingVariant] = useState(null);
  const [generatingBlend, setGeneratingBlend] = useState(null);
  const [feedbackInput, setFeedbackInput] = useState("");
  const [error, setError] = useState(null);
  const [overflow, setOverflow] = useState({ detected: false, px: 0 });
  const [extracting, setExtracting] = useState(false);
  const [whitepaperText, setWhitepaperText] = useState("");
  const [whitepaperFileName, setWhitepaperFileName] = useState("");
  const [processingAssets, setProcessingAssets] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].id);
  const [reviewingVariantA, setReviewingVariantA] = useState(false);
  const [extractingBrand, setExtractingBrand] = useState(false);
  const [generatingStream, setGeneratingStream] = useState("");
  const [isStreamThinking, setIsStreamThinking] = useState(false);
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const iframeRef = useRef(null);
  useEffect(() => { loadPagers(); }, []);

  // Overflow detection — fires when activeHtml changes and iframe loads
  useEffect(() => {
    if (!iframeRef.current || !activeHtml) return;
    const iframe = iframeRef.current;
    const check = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc || !doc.body) return;
        const pages = doc.querySelectorAll(".page");
        let maxOverflow = 0;
        if (pages.length > 0) {
          pages.forEach((p) => {
            const o = p.scrollHeight - p.clientHeight;
            if (o > 4) maxOverflow = Math.max(maxOverflow, o);
          });
        } else {
          const o = doc.body.scrollHeight - LETTER_H;
          if (o > 4) maxOverflow = o;
        }
        const detected = maxOverflow > 0;
        setOverflow({ detected, px: maxOverflow });
        if (detected && activePager?.id && activeVersionId) {
          api.onePager.patchOverflow(activePager.id, activeVersionId, { overflowDetected: true, overflowPx: maxOverflow }).catch(() => {});
        }
      } catch (_) {}
    };
    iframe.onload = check;
    if (iframe.contentDocument?.readyState === "complete") check();
  }, [activeHtml, activeVersionId]);

  const loadPagers = async () => {
    setLoading(true);
    try {
      const data = await api.onePager.list();
      setPagers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startNew = async () => {
    const id = `op-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const pager = {
      id,
      companyName: "",
      companyUrl: "",
      engagementContext: { defenseAngle: "", audience: "", distribution: "", flags: "" },
      brandVoice: "",
      logoBase64: null,
      logoMimeType: null,
      productImages: [],
      variantVersionIds: {},
      activeVersionId: null,
      generationCount: 0,
      status: "draft",
      createdBy: user?.email || user?.name || "unknown",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      await api.onePager.save(pager);
      setActivePager(pager);
      setView("build");
      setBuildStep(1);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const openPager = async (pager) => {
    try {
      const { pager: full, versions } = await api.onePager.get(pager.id);
      setActivePager({ ...full });
      setVariants(versions || []);
      setError(null);

      // Load active version HTML if present
      if (full.activeVersionId) {
        const vId = full.activeVersionId;
        const onePagerId = full.id;
        const ver = await api.onePager.getVersion(onePagerId, vId);
        setActiveVersionId(vId);
        setActiveHtml(ver.html || "");
        // Find which variant this is
        const variantKeys = Object.entries(full.variantVersionIds || {});
        const matched = variantKeys.find(([, v]) => v === vId);
        setActiveVariant(matched ? matched[0] : null);
        // Map stubs into variant format — include A/B/C and any blends (D/E)
        const stubs = ["A", "B", "C", "D", "E"].map((v) => {
          const versionId = full.variantVersionIds?.[v];
          const stub = versions.find((ver) => ver.id === versionId);
          return stub ? { ...stub, variant: v } : null;
        }).filter(Boolean);
        setVariants(stubs.length ? stubs : versions);
        setView("preview");
      } else {
        // No variants yet — go to build
        setView("build");
        setBuildStep(1);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const deletePager = async (id) => {
    if (!window.confirm("Delete this one-pager and all versions? This cannot be undone.")) return;
    try {
      await api.onePager.delete(id);
      setPagers((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const savePager = useCallback(async (updates = {}) => {
    const updated = { ...activePager, ...updates, updatedAt: new Date().toISOString() };
    setActivePager(updated);
    await api.onePager.save(updated);
    return updated;
  }, [activePager]);

  // ─── Step 1 form helpers ───
  const updateContext = (field, val) =>
    setActivePager((p) => ({ ...p, engagementContext: { ...p.engagementContext, [field]: val } }));

  // ─── Step 2 asset handlers ───
  const handleWhitepaperUpload = async ([file]) => {
    if (!file || !activePager) return;
    setExtracting(true);
    setError(null);
    try {
      const { text } = await extractOnePagerText(activePager.id, file);
      setWhitepaperText(text);
      setWhitepaperFileName(file.name);
    } catch (e) {
      setError(`White paper extraction failed: ${e.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleLogoUpload = async ([file]) => {
    if (!file || !activePager) return;
    setProcessingAssets(true);
    try {
      const { base64, mimeType } = await compressImage(file, 300, 0.85);
      await savePager({ logoBase64: base64, logoMimeType: mimeType });
    } catch (e) {
      setError(`Logo processing failed: ${e.message}`);
    } finally {
      setProcessingAssets(false);
    }
  };

  const handleProductImages = async (files) => {
    if (!activePager) return;
    const remaining = 3 - (activePager.productImages?.length || 0);
    const toProcess = files.slice(0, remaining);
    if (!toProcess.length) return;
    setProcessingAssets(true);
    try {
      const compressed = await Promise.all(toProcess.map((f) => compressImage(f, 640, 0.80)));
      const newImages = compressed.map((c, i) => ({ base64: c.base64, mimeType: c.mimeType, caption: toProcess[i].name.replace(/\.[^.]+$/, "") }));
      await savePager({ productImages: [...(activePager.productImages || []), ...newImages] });
    } catch (e) {
      setError(`Image processing failed: ${e.message}`);
    } finally {
      setProcessingAssets(false);
    }
  };

  const removeProductImage = async (idx) => {
    const updated = (activePager.productImages || []).filter((_, i) => i !== idx);
    await savePager({ productImages: updated });
  };

  // ─── Brand extraction ───
  const handleExtractBrand = async () => {
    if (!activePager?.companyUrl) return;
    setExtractingBrand(true);
    setError(null);
    try {
      const data = await callAnthropicDirect({
        model: selectedModel,
        max_tokens: 2048,
        tools: [{ type: "web_search_20250305", name: "web_search" }],
        messages: [{
          role: "user",
          content: `Visit ${activePager.companyUrl} and analyze the brand visual identity. Return ONLY a JSON object (no markdown fences, no explanation) with this exact structure:
{"colors":[{"hex":"#XXXXXX","name":"Color name","usage":"how it's used"}],"fonts":[{"name":"Font name","usage":"how it's used"}],"headerBg":"#XXXXXX","accentColor":"#XXXXXX","brandDescription":"One sentence describing the brand visual style."}
Include 2–4 colors and 1–2 fonts. headerBg should be the dominant dark/header color. accentColor should be the primary brand accent. Use actual hex values found on the site.`,
        }],
      }, { "anthropic-beta": "web-search-2025-03-05" });
      const textBlocks = (data.content || []).filter((b) => b.type === "text");
      const lastText = textBlocks[textBlocks.length - 1]?.text || "";
      const jsonMatch = lastText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("Brand extraction returned unexpected format.");
      const parsed = JSON.parse(jsonMatch[0]);
      await savePager({ brandData: parsed });
    } catch (e) {
      setError(`Brand extraction failed: ${e.message}`);
    } finally {
      setExtractingBrand(false);
    }
  };

  // ─── Generate ───
  const handleGenerate = async () => {
    if (!activePager?.id) return;
    setGenerating(true);
    setError(null);
    setView("generating");
    try {
      await api.onePager.save({ ...activePager, updatedAt: new Date().toISOString() });

      const ctx = activePager.engagementContext || {};
      const excerpt = whitepaperText.slice(0, 120000);
      const brandData = activePager.brandData;
      const useWebSearch = !!activePager.companyUrl && !brandData;
      const completedVariants = [];
      let firstVersionId = null;
      let variantAReview = "";

      const brandBlock = brandData
        ? `\nBRAND IDENTITY (pre-extracted from company website):\n${brandData.brandDescription || ""}\nHeader Background: ${brandData.headerBg || ""}\nAccent Color: ${brandData.accentColor || ""}\nColors:\n${(brandData.colors || []).map((c) => `  ${c.hex} — ${c.name} (${c.usage})`).join("\n")}\nFonts: ${(brandData.fonts || []).map((f) => `${f.name} (${f.usage})`).join(", ")}\nUse these exact colors and font families in the design.\n`
        : "";

      for (const variant of ["A", "B"]) {
        setGeneratingVariant(variant);
        setGeneratingStream("");

        const reviewBlock = variant === "B" && variantAReview
          ? `\nDESIGN CRITIQUE OF VARIANT A — use this to make Variant B better:\n${variantAReview}\n`
          : "";

        const userText = `Generate a two-page defense-industry capability brief for the following company.
${useWebSearch ? `\nCOMPANY WEBSITE: ${activePager.companyUrl}\nIMPORTANT: Use the web_search tool to visit this website and identify the company's brand colors, typography, and visual identity before designing. Incorporate their actual brand palette into the design.\n` : ""}${brandBlock}${reviewBlock}
COMPANY: ${activePager.companyName}
DEFENSE ANGLE: ${ctx.defenseAngle || "Not specified"}
TARGET AUDIENCE: ${ctx.audience || "General BD"}
DISTRIBUTION CONTEXT: ${ctx.distribution || "General BD"}
NOTES / FLAGS: ${ctx.flags || "None"}
BRAND VOICE: ${activePager.brandVoice || "Professional, direct, mission-focused. Trust earned through specificity — not hype."}

TECHNICAL WHITE PAPER CONTENT:
${excerpt || "(No white paper provided — generate based on company name and context only)"}

${VARIANT_INSTRUCTIONS[variant]}

Return only the complete HTML document.`;

        const body = {
          model: selectedModel,
          max_tokens: 16000,
          system: SYSTEM_PROMPT,
          ...(useWebSearch ? { tools: [{ type: "web_search_20250305", name: "web_search" }] } : {}),
          messages: [{ role: "user", content: [{ type: "text", text: userText }, ...buildImageContent(activePager)] }],
        };
        const extraHeaders = useWebSearch ? { "anthropic-beta": "web-search-2025-03-05" } : {};
        const html = await callAnthropicStream(body, extraHeaders, (_, full) => setGeneratingStream(full));

        const result = await api.onePager.generate(activePager.id, variant, html);
        if (result.error) throw new Error(`Variant ${variant}: ${result.error}`);
        completedVariants.push(result);
        if (!firstVersionId) firstVersionId = result.versionId;

        // After A: self-critique to feed into B
        if (variant === "A") {
          setReviewingVariantA(true);
          setGeneratingStream("");
          try {
            const reviewData = await callAnthropicDirect({
              model: selectedModel,
              max_tokens: 600,
              messages: [{
                role: "user",
                content: `You just designed Variant A of a defense capability brief. Review your own work critically.\n\nHTML:\n${html}\n\nProvide a concise self-critique (4–6 bullet points) covering:\n- Spacing or padding inconsistencies\n- Typography hierarchy issues\n- Visual balance or layout weaknesses\n- 1–2 specific creative improvements\n\nBe direct and specific. This critique will be used to make Variant B better.`,
              }],
            });
            variantAReview = reviewData.content?.[0]?.text || "";
          } catch (_) {
            // Review failure is non-fatal — B still generates without it
          }
          setReviewingVariantA(false);
        }
      }
      setGeneratingVariant(null);

      const fullVer = await api.onePager.getVersion(activePager.id, firstVersionId);
      setVariants(completedVariants.map((v) => ({ id: v.versionId, variant: v.variant, variantLabel: v.variantLabel, overflowDetected: false })));
      setActiveVariant("A");
      setActiveVersionId(firstVersionId);
      setActiveHtml(fullVer.html || "");

      const refreshed = await api.onePager.get(activePager.id);
      // Spread only non-asset fields — GET /one-pager/{id} strips logoBase64/productImages
      // to keep responses lean, so we must not overwrite them in local state.
      setActivePager((p) => ({
        ...p,
        variantVersionIds: refreshed.pager.variantVersionIds || p.variantVersionIds,
        activeVersionId: refreshed.pager.activeVersionId,
        generationCount: refreshed.pager.generationCount,
        status: refreshed.pager.status,
        updatedAt: refreshed.pager.updatedAt,
      }));
      setPagers((prev) => prev.map((p) => p.id === activePager.id ? { ...p, status: "ready", generationCount: refreshed.pager.generationCount } : p));

      setView("preview");
    } catch (e) {
      setGeneratingVariant(null);
      setError(e.message);
      setView("build");
      setBuildStep(2);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Blend variants (D + E) from user feedback ───
  const handleBlend = async () => {
    if (!feedbackInput.trim() || !activePager?.id) return;
    setGeneratingBlend("D");
    setError(null);
    const ctx = activePager.engagementContext || {};
    const variantIds = activePager.variantVersionIds || {};
    try {
      // Fetch actual HTML for A and B so Claude can copy specific elements
      const [verA, verB] = await Promise.all([
        variantIds.A ? api.onePager.getVersion(activePager.id, variantIds.A) : null,
        variantIds.B ? api.onePager.getVersion(activePager.id, variantIds.B) : null,
      ]);
      const htmlA = verA ? restorePlaceholders(verA.html || "", activePager) : "";
      const htmlB = verB ? restorePlaceholders(verB.html || "", activePager) : "";

      for (const variant of ["D", "E"]) {
        setGeneratingBlend(variant);
        const blendText = `You generated two one-pager variants for ${activePager.companyName}. The user has reviewed them and given feedback. Your job is to synthesize a new version that takes the specific elements they liked — don't start from scratch.

USER FEEDBACK: "${feedbackInput}"

${htmlA ? `VARIANT A HTML:\n${htmlA}` : ""}

${htmlB ? `VARIANT B HTML:\n${htmlB}` : ""}

COMPANY: ${activePager.companyName}
DEFENSE ANGLE: ${ctx.defenseAngle || "Not specified"}
TARGET AUDIENCE: ${ctx.audience || "General BD"}

${variant === "D"
  ? "BLEND 1 — FAITHFUL: Adopt the exact layout sections and content the user said they liked. If they said they loved Page 1 of A, use that Page 1 structure directly. Be conservative — keep what works, only change what the user asked to change."
  : "BLEND 2 — ELEVATED: Take what the user liked and push it further. Same direction, higher ambition. More polished, more visually striking, without losing what earned their approval."}

Return only the complete HTML document.`;

        const html = await callAnthropicStream({
          model: selectedModel,
          max_tokens: 16000,
          system: SYSTEM_PROMPT,
          messages: [{ role: "user", content: blendText }],
        }, {}, (_, full) => setGeneratingStream(full));

        const result = await api.onePager.generate(activePager.id, variant, html);
        if (result.error) throw new Error(result.error);

        setVariants((prev) => {
          const without = prev.filter((v) => v.variant !== variant);
          return [...without, { id: result.versionId, variant, variantLabel: result.variantLabel, overflowDetected: false }];
        });

        if (variant === "D") {
          const fullVer = await api.onePager.getVersion(activePager.id, result.versionId);
          setActiveVariant("D");
          setActiveVersionId(result.versionId);
          setActiveHtml(fullVer.html || "");
          setOverflow({ detected: false, px: 0 });
        }

        const refreshed = await api.onePager.get(activePager.id);
        setActivePager((p) => ({ ...p, variantVersionIds: refreshed.pager.variantVersionIds || p.variantVersionIds }));
      }
      setFeedbackInput("");
    } catch (e) {
      setError(e.message);
    } finally {
      setGeneratingBlend(null);
    }
  };

  // ─── Switch between variants ───
  const selectVariant = async (variant) => {
    const versionId = activePager?.variantVersionIds?.[variant];
    if (!versionId || !activePager?.id) return;
    try {
      const ver = await api.onePager.getVersion(activePager.id, versionId);
      setActiveVariant(variant);
      setActiveVersionId(versionId);
      setActiveHtml(ver.html || "");
      setOverflow({ detected: false, px: 0 });
    } catch (e) {
      setError(e.message);
    }
  };

  // ─── PDF export ───
  const handleExport = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  // ─── Download HTML + Claude prompt ───
  const handleDownload = () => {
    if (!activeHtml || !activePager) return;
    setShowDownloadPanel(true);
    const variantInfo = variants.find((v) => v.variant === activeVariant);
    const slug = (activePager.companyName || "one-pager").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const label = variantInfo?.variantLabel || activeVariant || "variant";
    const labelSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const ctx = activePager.engagementContext || {};

    const htmlBlob = new Blob([activeHtml], { type: "text/html" });
    const htmlUrl = URL.createObjectURL(htmlBlob);
    const a1 = document.createElement("a");
    a1.href = htmlUrl;
    a1.download = `${slug}-${labelSlug}.html`;
    a1.click();
    URL.revokeObjectURL(htmlUrl);

    const promptLines = [
      `You are helping refine a defense-industry capability brief for ${activePager.companyName}.`,
      ``,
      `CONTEXT`,
      `Company: ${activePager.companyName}`,
      ctx.defenseAngle ? `Defense Angle: ${ctx.defenseAngle}` : null,
      ctx.audience ? `Target Audience: ${ctx.audience}` : null,
      ctx.distribution ? `Distribution: ${ctx.distribution}` : null,
      activePager.brandVoice ? `Brand Voice: ${activePager.brandVoice}` : null,
      ctx.flags ? `Notes: ${ctx.flags}` : null,
      ``,
      `I'm attaching two files to this conversation:`,
      `1. ${slug}-${labelSlug}.html — the current one-pager (${label})`,
      `2. Our technical white paper — use this for accurate product details and messaging`,
      ``,
      `Please review the HTML one-pager carefully. Then either ask me what changes I'd like to make,`,
      `or suggest 2-3 specific areas where the design or content could be strengthened.`,
    ].filter((l) => l !== null).join("\n");

    setTimeout(() => {
      const promptBlob = new Blob([promptLines], { type: "text/plain" });
      const promptUrl = URL.createObjectURL(promptBlob);
      const a2 = document.createElement("a");
      a2.href = promptUrl;
      a2.download = `${slug}-claude-prompt.txt`;
      a2.click();
      URL.revokeObjectURL(promptUrl);
    }, 150);
  };

  // ─── Scale to fit ───
  const previewContainerRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.72);
  useEffect(() => {
    if (!previewContainerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const availW = entry.contentRect.width - 32;
      if (availW > 0) setPreviewScale(Math.min(1, availW / LETTER_W));
    });
    obs.observe(previewContainerRef.current);
    return () => obs.disconnect();
  }, [view]);

  // ───────────────────────────────────────────────
  // VIEWS
  // ───────────────────────────────────────────────

  // ─── LIST ───
  if (view === "list") {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 14, color: colors.textDim }}>
            {loading ? "Loading..." : `${pagers.length} one-pager${pagers.length !== 1 ? "s" : ""}`}
          </div>
          <button onClick={startNew} style={s.btnPrimary}>+ New One-Pager</button>
        </div>
        {error && <div style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${colors.danger}`, borderRadius: 8, padding: "12px 16px", color: colors.danger, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <div style={s.card}>
          {!loading && pagers.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: colors.textDim, fontSize: 14 }}>
              No one-pagers yet.{" "}
              <button onClick={startNew} style={{ background: "none", border: "none", color: colors.accent, cursor: "pointer", fontSize: 14, fontFamily: font }}>
                Create the first one →
              </button>
            </div>
          )}
          {pagers.map((p) => (
            <PagerRow key={p.id} pager={p} onOpen={openPager} onDelete={deletePager} />
          ))}
        </div>
      </div>
    );
  }

  // ─── BUILD (wizard steps 1 & 2) ───
  if (view === "build") {
    const canAdvance1 = activePager?.companyName?.trim().length > 0;
    const canGenerate = activePager?.companyName?.trim().length > 0;

    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <button onClick={() => { setView("list"); loadPagers(); }} style={{ background: "none", border: "none", color: colors.textDim, cursor: "pointer", fontFamily: font, fontSize: 13, padding: 0 }}>← All One-Pagers</button>
          <div style={{ display: "flex", gap: 8 }}>
            {[1, 2].map((n) => (
              <div key={n} style={{
                width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: buildStep === n ? colors.accent : buildStep > n ? colors.success : colors.border,
                color: buildStep >= n ? "#fff" : colors.textDim, fontSize: 12, fontWeight: 700,
              }}>{n}</div>
            ))}
          </div>
          <span style={{ fontSize: 13, color: colors.textMuted }}>{buildStep === 1 ? "Company & Context" : "Assets & Generation"}</span>
        </div>

        {error && <div style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${colors.danger}`, borderRadius: 8, padding: "12px 16px", color: colors.danger, fontSize: 13, marginBottom: 18 }}>{error}</div>}

        {/* ── STEP 1 ── */}
        {buildStep === 1 && (
          <div style={s.card}>
            <div style={s.sectionHead}>Company & Engagement</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={s.fieldWrap}>
                <label style={s.label}>Company Name *</label>
                <input style={s.input} value={activePager?.companyName || ""} placeholder="e.g. Titin KM Biomedical"
                  onChange={(e) => setActivePager((p) => ({ ...p, companyName: e.target.value }))} />
              </div>
              <div style={s.fieldWrap}>
                <label style={s.label}>Company Website URL <span style={{ color: colors.textDim, fontWeight: 400 }}>(for brand colors)</span></label>
                <input style={s.input} value={activePager?.companyUrl || ""} placeholder="e.g. https://titinkmbiomedical.com"
                  onChange={(e) => setActivePager((p) => ({ ...p, companyUrl: e.target.value }))} />
              </div>
            </div>

            <div style={s.fieldWrap}>
              <label style={s.label}>Defense Angle</label>
              <textarea style={{ ...s.input, height: 72, resize: "vertical" }}
                placeholder="e.g. Soldier Performance, Port Security"
                value={activePager?.engagementContext?.defenseAngle || ""}
                onChange={(e) => updateContext("defenseAngle", e.target.value)} />
            </div>

            <div style={s.fieldWrap}>
              <label style={s.label}>Target Audience</label>
              <textarea style={{ ...s.input, height: 72, resize: "vertical" }}
                placeholder="e.g. Program Office, Prime, DIU"
                value={activePager?.engagementContext?.audience || ""}
                onChange={(e) => updateContext("audience", e.target.value)} />
            </div>

            <div style={s.fieldWrap}>
              <label style={s.label}>Distribution Context</label>
              <textarea style={{ ...s.input, height: 72, resize: "vertical" }}
                placeholder="e.g. RFP Response, Trade Show, Cold Outreach"
                value={activePager?.engagementContext?.distribution || ""}
                onChange={(e) => updateContext("distribution", e.target.value)} />
            </div>

            <div style={s.fieldWrap}>
              <label style={s.label}>Notes / Flags <span style={{ color: colors.textDim, fontWeight: 400 }}>(optional)</span></label>
              <textarea style={{ ...s.input, height: 72, resize: "vertical" }}
                placeholder="e.g. Don't mention funding stage. Emphasize DoD validation."
                value={activePager?.engagementContext?.flags || ""}
                onChange={(e) => updateContext("flags", e.target.value)} />
            </div>

            <div style={s.fieldWrap}>
              <label style={s.label}>Brand Voice <span style={{ color: colors.textDim, fontWeight: 400 }}>(optional)</span></label>
              <textarea style={{ ...s.input, height: 72, resize: "vertical" }}
                placeholder="e.g. Direct, mission-focused, trust earned through specificity. Not hype."
                value={activePager?.brandVoice || ""}
                onChange={(e) => setActivePager((p) => ({ ...p, brandVoice: e.target.value }))} />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
              <button onClick={() => { setView("list"); loadPagers(); }} style={s.btnSecondary}>Cancel</button>
              <button disabled={!canAdvance1} onClick={async () => { await savePager(); setBuildStep(2); }}
                style={{ ...s.btnPrimary, opacity: canAdvance1 ? 1 : 0.5, cursor: canAdvance1 ? "pointer" : "not-allowed" }}>
                Next: Upload Assets →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2 ── */}
        {buildStep === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={s.card}>
              <div style={s.sectionHead}>White Paper</div>
              <DropZone
                label="White Paper (PDF or DOCX)"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                multiple={false}
                onFiles={handleWhitepaperUpload}
                files={whitepaperFileName ? [whitepaperFileName] : []}
                hint="Drop PDF or DOCX here"
              />
              {extracting && <div style={{ fontSize: 12, color: colors.accent, marginTop: 10 }}>Extracting text…</div>}
              {whitepaperText && !extracting && (
                <div style={{ fontSize: 12, color: colors.success, marginTop: 10 }}>
                  ✓ {whitepaperText.length.toLocaleString()} characters extracted from {whitepaperFileName}
                </div>
              )}
            </div>

            <div style={s.card}>
              <div style={s.sectionHead}>Brand Assets</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <DropZone
                    label="Company Logo (PNG, SVG, JPG)"
                    accept="image/*"
                    multiple={false}
                    onFiles={handleLogoUpload}
                    hint="Drop logo here"
                    files={activePager?.logoBase64 ? ["Logo uploaded ✓"] : []}
                  />
                  {activePager?.logoBase64 && (
                    <div style={{ marginTop: 10 }}>
                      <img src={activePager.logoBase64} alt="Logo preview" style={{ maxHeight: 40, maxWidth: "100%", objectFit: "contain" }} />
                    </div>
                  )}
                </div>
                <div>
                  <label style={s.label}>Product Images (up to 3)</label>
                  {(activePager?.productImages || []).length < 3 && (
                    <DropZone
                      label=""
                      accept="image/*"
                      multiple={true}
                      onFiles={handleProductImages}
                      hint="Drop product photos here"
                      files={[]}
                    />
                  )}
                  <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {(activePager?.productImages || []).map((img, i) => (
                      <div key={i} style={{ position: "relative" }}>
                        <img src={img.base64} alt={img.caption} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: `1px solid ${colors.border}` }} />
                        <button onClick={() => removeProductImage(i)} style={{
                          position: "absolute", top: -6, right: -6, width: 18, height: 18,
                          borderRadius: "50%", background: colors.danger, border: "none", color: "#fff",
                          fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {processingAssets && <div style={{ fontSize: 12, color: colors.accent, marginTop: 10 }}>Processing assets…</div>}
            </div>

            {/* Brand detection card */}
            {activePager?.companyUrl && (
              <div style={s.card}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: activePager.brandData ? 16 : 0 }}>
                  <div style={s.sectionHead}>Brand Identity</div>
                  <button
                    onClick={handleExtractBrand}
                    disabled={extractingBrand}
                    style={{ ...s.btnSecondary, fontSize: 12, padding: "7px 14px",
                      opacity: extractingBrand ? 0.5 : 1,
                      cursor: extractingBrand ? "not-allowed" : "pointer" }}
                  >
                    {extractingBrand ? "Analyzing website…" : activePager.brandData ? "Re-extract Brand" : "Extract Brand Colors & Fonts"}
                  </button>
                </div>
                {extractingBrand && (
                  <div style={{ fontSize: 12, color: colors.accent, marginTop: 8 }}>Visiting {activePager.companyUrl} to analyze brand identity…</div>
                )}
                {activePager.brandData && !extractingBrand && (
                  <div>
                    {activePager.brandData.brandDescription && (
                      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 14, lineHeight: 1.5 }}>{activePager.brandData.brandDescription}</div>
                    )}
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                      {(activePager.brandData.colors || []).length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textDim, textTransform: "uppercase", marginBottom: 8 }}>Colors</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {(activePager.brandData.colors || []).map((c, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 4, background: c.hex, border: `1px solid ${colors.border}`, flexShrink: 0 }} />
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{c.hex}</div>
                                  <div style={{ fontSize: 11, color: colors.textDim }}>{c.name}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(activePager.brandData.fonts || []).length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textDim, textTransform: "uppercase", marginBottom: 8 }}>Fonts</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {(activePager.brandData.fonts || []).map((f, i) => (
                              <div key={i} style={{
                                padding: "4px 10px", borderRadius: 20, fontSize: 12,
                                background: colors.surface, border: `1px solid ${colors.border}`,
                                color: colors.text,
                              }}>
                                {f.name} <span style={{ color: colors.textDim, fontSize: 11 }}>· {f.usage}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!activePager.brandData && !extractingBrand && (
                  <div style={{ fontSize: 12, color: colors.textDim, marginTop: 8 }}>
                    Extract brand colors and fonts from the company website before generating — this improves design accuracy and removes the need for Claude to browse the web during generation.
                  </div>
                )}
              </div>
            )}

            {error && <div style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${colors.danger}`, borderRadius: 8, padding: "12px 16px", color: colors.danger, fontSize: 13 }}>{error}</div>}

            {/* Model selector */}
            <div style={s.card}>
              <div style={s.sectionHead}>Model</div>
              <div style={{ display: "flex", gap: 10 }}>
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedModel(m.id)}
                    style={{
                      flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer", textAlign: "left",
                      border: `1px solid ${selectedModel === m.id ? colors.accent : colors.border}`,
                      background: selectedModel === m.id ? colors.accentDim : "transparent",
                      fontFamily: font, transition: "all 0.15s",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: selectedModel === m.id ? colors.accent : colors.text, marginBottom: 2 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: colors.textDim }}>{m.note}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={() => setBuildStep(1)} style={s.btnSecondary}>← Back</button>
              <button
                disabled={!canGenerate || extracting || processingAssets || extractingBrand}
                onClick={handleGenerate}
                style={{ ...s.btnPrimary, opacity: (canGenerate && !extracting && !processingAssets && !extractingBrand) ? 1 : 0.5, cursor: (canGenerate && !extracting && !processingAssets && !extractingBrand) ? "pointer" : "not-allowed" }}
              >
                Generate 3 Variants →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── GENERATING ───
  if (view === "generating") {
    const VARIANT_LABELS_UI = { A: "Impact First", B: "Precision & Craft" };
    const variantOrder = ["A", "B"];
    const currentIdx = generatingVariant ? variantOrder.indexOf(generatingVariant) : -1;
    const streamTail = generatingStream.length > 0 ? generatingStream.slice(-800) : "";
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", minHeight: 400, paddingTop: 40 }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            border: `3px solid ${colors.border}`, borderTop: `3px solid ${colors.accent}`,
            animation: "spin 0.9s linear infinite",
          }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: colors.text }}>
            {reviewingVariantA
              ? "Reviewing Variant A…"
              : generatingVariant ? `Generating Variant ${generatingVariant} — ${VARIANT_LABELS_UI[generatingVariant]}`
              : "Finishing up…"}
          </div>
          {generatingStream.length > 0 && !reviewingVariantA && (
            <div style={{ fontSize: 12, color: colors.textDim, fontFamily: "monospace" }}>
              {generatingStream.length.toLocaleString()} chars
            </div>
          )}
        </div>
        {reviewingVariantA && (
          <div style={{ fontSize: 13, color: colors.textDim, marginBottom: 8 }}>
            Finding spacing issues and creative improvements to carry into Variant B
          </div>
        )}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          {variantOrder.map((v, i) => (
            <div key={v} style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: i < currentIdx ? colors.success : i === currentIdx ? colors.accent : colors.border,
              color: i <= currentIdx ? "#fff" : colors.textDim,
              transition: "background 0.3s",
            }}>
              {i < currentIdx ? "✓ " : ""}{v} · {VARIANT_LABELS_UI[v]}
            </div>
          ))}
        </div>
        {/* Live code stream */}
        {streamTail && (
          <div style={{
            width: "100%", maxWidth: 760,
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10,
            padding: "14px 18px", overflow: "hidden", position: "relative",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textDim, textTransform: "uppercase", marginBottom: 8 }}>
              Live Output
            </div>
            <pre style={{
              margin: 0, fontFamily: "monospace", fontSize: 11, color: colors.textMuted,
              lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all",
              maxHeight: 260, overflow: "hidden",
            }}>
              {streamTail}
            </pre>
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 48,
              background: `linear-gradient(transparent, ${colors.surface})`,
              borderRadius: "0 0 10px 10px", pointerEvents: "none",
            }} />
          </div>
        )}
      </div>
    );
  }

  // ─── PREVIEW ───
  if (view === "preview") {
    const scaledW = Math.round(LETTER_W * previewScale);
    const scaledH = Math.round(LETTER_H * 2 * previewScale); // 2-page document

    return (
      <div style={{ display: "flex", gap: 0, height: "calc(100vh - 140px)", minHeight: 600 }}>
        {/* Left panel: variant tabs + controls */}
        <div style={{
          width: 220, flexShrink: 0, display: "flex", flexDirection: "column",
          borderRight: `1px solid ${colors.border}`, paddingRight: 20, gap: 8,
        }}>
          <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ ...s.sectionHead, marginBottom: 4 }}>Variants</div>
            {variants.filter((v) => ["A", "B"].includes(v.variant)).map((v) => (
              <button key={v.variant} onClick={() => selectVariant(v.variant)} style={{
                padding: "10px 14px", borderRadius: 8, border: `1px solid ${activeVariant === v.variant ? colors.accent : colors.border}`,
                background: activeVariant === v.variant ? colors.accentDim : "transparent",
                cursor: "pointer", fontFamily: font, textAlign: "left", color: colors.text, flexShrink: 0,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: activeVariant === v.variant ? colors.accent : colors.textDim, marginBottom: 3 }}>
                  VARIANT {v.variant}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{v.variantLabel}</div>
                {v.overflowDetected && <div style={{ fontSize: 10, color: colors.warning, marginTop: 3 }}>⚠ Overflow</div>}
              </button>
            ))}

            {/* Blend variants (D/E) */}
            {variants.filter((v) => ["D", "E"].includes(v.variant)).length > 0 && (
              <>
                <div style={{ ...s.sectionHead, marginTop: 10, marginBottom: 4 }}>Blends</div>
                {variants.filter((v) => ["D", "E"].includes(v.variant)).map((v) => (
                  <button key={v.variant} onClick={() => selectVariant(v.variant)} style={{
                    padding: "10px 14px", borderRadius: 8, border: `1px solid ${activeVariant === v.variant ? colors.accent : colors.border}`,
                    background: activeVariant === v.variant ? colors.accentDim : "transparent",
                    cursor: "pointer", fontFamily: font, textAlign: "left", color: colors.text, flexShrink: 0,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: activeVariant === v.variant ? colors.accent : colors.textDim, marginBottom: 3 }}>
                      {v.variantLabel.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>AI Synthesis</div>
                  </button>
                ))}
              </>
            )}


            {/* Blend generation panel */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
              <div style={{ ...s.sectionHead, marginBottom: 8 }}>Blend Variants</div>
              {generatingBlend && (
                <div style={{ background: colors.accentDim, border: `1px solid ${colors.accent}44`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", border: `2px solid ${colors.accent}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                    <div style={{ fontSize: 12, color: colors.accent, fontWeight: 600 }}>
                      {generatingBlend === "D" ? "Generating Blend 1 of 2…" : "Generating Blend 2 of 2…"}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4 }}>This takes 30–60 seconds per blend.</div>
                </div>
              )}
              {!activePager?.variantVersionIds?.E && (
                <>
                  {!generatingBlend && (
                    <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 8, lineHeight: 1.5 }}>
                      Review A and B above, then describe what you liked to generate 2 synthesized blends.
                    </div>
                  )}
                  <textarea
                    value={feedbackInput}
                    onChange={(e) => setFeedbackInput(e.target.value)}
                    placeholder={'e.g. "Love the design of A, but prefer the verbiage from B"'}
                    style={{ ...s.input, height: 72, resize: "vertical", marginBottom: 8, fontSize: 12 }}
                    disabled={!!generatingBlend}
                  />
                  <button
                    disabled={!feedbackInput.trim() || !!generatingBlend}
                    onClick={handleBlend}
                    style={{ ...s.btnPrimary, textAlign: "center", width: "100%", fontSize: 13, padding: "9px 14px",
                      opacity: (!feedbackInput.trim() || !!generatingBlend) ? 0.5 : 1,
                      cursor: (!feedbackInput.trim() || !!generatingBlend) ? "not-allowed" : "pointer" }}
                  >
                    Generate 2 Blends →
                  </button>
                </>
              )}
            </div>
          </div>

          {overflow.detected && (
            <div style={{ background: "rgba(245,158,11,0.12)", border: `1px solid ${colors.warning}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: colors.warning, flexShrink: 0 }}>
              ⚠ Overflows by ~{overflow.px}px
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
            <button onClick={handleDownload} disabled={!activeHtml} style={{ ...s.btnPrimary, textAlign: "center", opacity: activeHtml ? 1 : 0.5 }}>
              Download HTML + Prompt
            </button>
            <button onClick={() => { setView("build"); setBuildStep(2); }} style={{ ...s.btnSecondary, textAlign: "center", fontSize: 12 }}>
              ← Edit Inputs
            </button>
          </div>
        </div>

        {/* Center: iframe preview */}
        <div ref={previewContainerRef} style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", justifyContent: "center" }}>
          {activeHtml ? (
            <div style={{ width: scaledW, height: scaledH, flexShrink: 0, position: "relative" }}>
              <div style={{ width: LETTER_W, height: LETTER_H * 2, transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
                <iframe
                  ref={iframeRef}
                  srcDoc={activeHtml}
                  style={{ width: LETTER_W, height: LETTER_H * 2, border: "none", display: "block" }}
                  sandbox="allow-same-origin allow-modals"
                  title="One-pager preview"
                />
              </div>
            </div>
          ) : (
            <div style={{ color: colors.textDim, fontSize: 14, alignSelf: "center" }}>Select a variant to preview</div>
          )}
        </div>

        {/* Right: download instructions panel */}
        {showDownloadPanel && (
          <div style={{
            width: 320, flexShrink: 0,
            borderLeft: `1px solid ${colors.border}`,
            display: "flex", flexDirection: "column",
            height: "100%", overflowY: "auto",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 12px", borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Next Steps</div>
              <button onClick={() => setShowDownloadPanel(false)} style={{ background: "none", border: "none", color: colors.textDim, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px", fontFamily: font }}>×</button>
            </div>

            <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { n: 1, text: "Open Claude.ai" },
                { n: 2, text: "Open the downloaded text file. Copy and paste the prompt directly into Claude." },
                { n: 3, text: "Attach the downloaded HTML file and your white paper to the same message." },
                { n: 4, text: "Send the message." },
                { n: 5, text: "Edit as needed. Ask Claude to show edits as an artifact if helpful." },
              ].map(({ n, text }) => (
                <div key={n} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: colors.accent, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, color: "#fff",
                  }}>{n}</div>
                  <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.55, paddingTop: 3 }}>{text}</div>
                </div>
              ))}

              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 14, marginTop: 2 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: colors.textDim, marginBottom: 10 }}>Edit Checklist</div>
                {[
                  "Remove all AI-style em dashes",
                  "Ensure no text is cut off",
                  "Verify brand colors are accurate",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${colors.border}`, flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.5 }}>{item}</div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 14, marginTop: 2, display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  { n: 7, text: 'Ask Claude to export the final version as a PDF.' },
                  { n: 8, text: 'Review the PDF carefully — common issues are text overlap and content cutoff at page boundaries.' },
                ].map(({ n, text }) => (
                  <div key={n} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: colors.surfaceAlt, border: `1.5px solid ${colors.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, color: colors.textDim,
                    }}>{n}</div>
                    <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.55, paddingTop: 3 }}>{text}</div>
                  </div>
                ))}
              </div>

              <button onClick={handleDownload} style={{ ...s.btnPrimary, textAlign: "center", marginTop: 4 }}>
                Re-download Files
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
